import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { StorageService } from 'src/app/services/storage.service';
import { BasicDataEnergySavingsComponent, EnergyBasicData } from '../basic-data-energy-savings/basic-data-energy-savings.component';
import { ConfigurationPage } from 'src/app/pages/configuration/configuration.page';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { EnergySavingsApiService, SummerCalcRequest, SummerCalcResponse, WinterCalcRequest, WinterCalcResponse, WinterSummerCalcResponse } from 'src/app/services/energy-savings-api.service';
import { WindowPayloadBuilderService } from 'src/app/services/window-payload-builder.service';
import { DEFAULT_ENERGY_SAVINGS_DATA } from 'src/app/utils/Constants';

@Component({
  selector: 'app-energy-savings-calculator',
  templateUrl: './energy-savings-calculator.component.html',
  styleUrls: ['./energy-savings-calculator.component.scss'],
  standalone: false,
})
export class EnergySavingsCalculatorComponent implements OnInit {
  isFormValid = false;
  eQty: number = 1;
  eYrs: number = 1;
  minQty = 1; // Absolute zero (or your preferred min)
  maxQty = 150; // Your preferred max
  minYrs = 1;
  maxYrs = 150;
  stepCount = 1; // Increment/decrement step
  selectedOption: "winter" | "summer" | "both" = "winter";
  basicData: EnergyBasicData | null = null;
  hasError = false;
  errorMessage = '';
  totalWindowArea: string = '0.00';

  winterResults: WinterCalcResponse | null = null;
  summerResults: SummerCalcResponse | null = null;
  bothResults: WinterSummerCalcResponse | null = null;
  // Debounce trigger
  private calculationSubscription: Subscription;

  // Track previous values
  private previousValues = {
    eQty: null as number | null,
    eYrs: null as number | null,
    selectedOption: null as string | null,
    basicData: null as any
  };
  private calculationTrigger$ = new Subject<{
    eQty: number;
    eYrs: number;
    selectedOption: string;
    basicData: any;
  }>();
  constructor(private modalController: ModalController, private storageService: StorageService, private energySavingsApi: EnergySavingsApiService, private windowPayloadBuilder: WindowPayloadBuilderService) {
    this.calculationSubscription = this.calculationTrigger$
      .pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) =>
          prev.eQty === curr.eQty &&
          prev.eYrs === curr.eYrs &&
          prev.selectedOption === curr.selectedOption &&
          JSON.stringify(prev.basicData) === JSON.stringify(curr.basicData)
        )
      )
      .subscribe(() => {
        console.log('Debounced calculation triggered');
        this.performCalculations();
      });
  }

  ngOnInit() { }

  async ionViewWillEnter() {
    await this.init();
  }

  private async init(): Promise<void> {
    try {
      await this.ensureComparedProjectExists();
      const res = await this.storageService.getProject();
      let storedProject = res ? JSON.parse(res) : null;
      if (storedProject?.energyBasicData) {
        this.basicData = storedProject.energyBasicData;
        if (this.basicData) {
          this.selectedOption = this.basicData.season;
        }
      }
      else {
        // Set default basic data for first time
        console.log('No existing basicData, setting defaults...');
        await this.setDefaultBasicData();
      }
      const savedResult = storedProject?.energySavingsResult;
      if (savedResult) {
        this.eQty = savedResult.windowsCount ?? 1;
        this.eYrs = savedResult.yearsCount ?? 1;
      }
      this.updateTotalWindowArea(storedProject);
      this.validateForm();
    } catch (error) {
      console.error('Error initializing spacer data:', error);
    }
  }

  private async ensureComparedProjectExists(): Promise<void> {
    const comparedProject = await this.storageService.getComparedProject();
    if (!comparedProject) {
      console.log('Compared project does not exist, creating default...');
      await this.storageService.saveComparedProject();
    } else {
      console.log('Compared project already exists');
    }
  }

  async onSeasonChange() {
    console.log('Season changed to:', this.selectedOption);
    // Load basic data for new season if it exists
    await this.loadBasicDataForSeason();
    // Trigger validation which will trigger calculation
    await this.validateForm();
  }

  private async loadBasicDataForSeason(): Promise<void> {
    try {
      const res = await this.storageService.getProject();
      const storedProject = res ? JSON.parse(res) : null;

      if (storedProject?.energyBasicData) {
        const savedBasicData = storedProject.energyBasicData;

        // Check if saved data matches current season
        if (savedBasicData.season === this.selectedOption) {
          this.basicData = savedBasicData;
          console.log('Loaded existing basic data for season:', this.selectedOption);
        } else {
          // Different season - create new basic data with defaults
          console.log('Season mismatch, creating new basic data...');

          if (this.selectedOption === 'winter') {
            this.basicData = {
              season: 'winter',
              winter: {
                fuelType: DEFAULT_ENERGY_SAVINGS_DATA.winter.fuelType,
                heatingCost: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCost,
                heatingCurrency: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCurrency,
                calorificValue: DEFAULT_ENERGY_SAVINGS_DATA.winter.calorificValue,
                heatingUnit: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingUnit,
                heatSeason: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatSeason,
                internalTemp: DEFAULT_ENERGY_SAVINGS_DATA.winter.internalTemp,
                externalTemp: DEFAULT_ENERGY_SAVINGS_DATA.winter.externalTemp,
                efficiency: DEFAULT_ENERGY_SAVINGS_DATA.winter.efficiency,
                costDelta: DEFAULT_ENERGY_SAVINGS_DATA.winter.costDelta
              }
            };
          } else if (this.selectedOption === 'summer') {
            this.basicData = {
              season: 'summer',
              summer: {
                powerType: DEFAULT_ENERGY_SAVINGS_DATA.summer.powerType,
                electricalCost: DEFAULT_ENERGY_SAVINGS_DATA.summer.electricalCost,
                electricalCurrency: DEFAULT_ENERGY_SAVINGS_DATA.summer.electricalCurrency,
                coolSeason: DEFAULT_ENERGY_SAVINGS_DATA.summer.coolSeason,
                internalTemp: DEFAULT_ENERGY_SAVINGS_DATA.summer.internalTemp,
                externalTemp: DEFAULT_ENERGY_SAVINGS_DATA.summer.externalTemp,
                efficiency: DEFAULT_ENERGY_SAVINGS_DATA.summer.efficiency,
                costDelta: DEFAULT_ENERGY_SAVINGS_DATA.summer.costDelta
              }
            };
          } else if (this.selectedOption === 'both') {
            this.basicData = {
              season: 'both',
              winter: {
                fuelType: DEFAULT_ENERGY_SAVINGS_DATA.winter.fuelType,
                heatingCost: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCost,
                heatingCurrency: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCurrency,
                calorificValue: DEFAULT_ENERGY_SAVINGS_DATA.winter.calorificValue,
                heatingUnit: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingUnit,
                heatSeason: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatSeason,
                internalTemp: DEFAULT_ENERGY_SAVINGS_DATA.winter.internalTemp,
                externalTemp: DEFAULT_ENERGY_SAVINGS_DATA.winter.externalTemp,
                efficiency: DEFAULT_ENERGY_SAVINGS_DATA.winter.efficiency,
                costDelta: DEFAULT_ENERGY_SAVINGS_DATA.winter.costDelta
              },
              summer: {
                powerType: DEFAULT_ENERGY_SAVINGS_DATA.summer.powerType,
                electricalCost: DEFAULT_ENERGY_SAVINGS_DATA.summer.electricalCost,
                electricalCurrency: DEFAULT_ENERGY_SAVINGS_DATA.summer.electricalCurrency,
                coolSeason: DEFAULT_ENERGY_SAVINGS_DATA.summer.coolSeason,
                internalTemp: DEFAULT_ENERGY_SAVINGS_DATA.summer.internalTemp,
                externalTemp: DEFAULT_ENERGY_SAVINGS_DATA.summer.externalTemp,
                efficiency: DEFAULT_ENERGY_SAVINGS_DATA.summer.efficiency,
                costDelta: DEFAULT_ENERGY_SAVINGS_DATA.summer.costDelta
              }
            };
          }

          // Save the new season data
          await this.storageService.updateProject({
            energyBasicData: this.basicData
          });
          console.log('New season basic data saved');
        }
      } else {
        // No saved data at all - shouldn't happen after init, but handle it
        await this.setDefaultBasicData();
      }
    } catch (error) {
      console.error('Error loading basic data for season:', error);
      this.basicData = null;
    }
  }

  async validate() {
    if (!this.isFormValid || !this.hasResults()) return;

    const bd = this.basicData;

    const energySavingsResult = {
      // ── meta  ──
      season: this.selectedOption,
      windowsCount: this.eQty,
      yearsCount: this.eYrs,

      // ── summerData (input params) — taken from energyBasicData.summer ──
      summerData: bd?.summer ? {
        coolseason: bd.summer.coolSeason,
        cost: bd.summer.electricalCost,
        costDelta: bd.summer.costDelta,
        currency: bd.summer.electricalCurrency,
        efficiency: bd.summer.efficiency,
        externalTemp: bd.summer.externalTemp,
        internalTemp: bd.summer.internalTemp,
        power: bd.summer.powerType,
        project: await this.getProjectName()
      } : null,

      // ── winterData (input params) — taken from energyBasicData.winter ──
      winterData: bd?.winter ? {
        calorific: bd.winter.calorificValue,
        cost: bd.winter.heatingCost,
        costDelta: bd.winter.costDelta,
        currency: bd.winter.heatingCurrency,
        efficiency: bd.winter.efficiency,
        externalTemp: bd.winter.externalTemp,
        heatSeason: bd.winter.heatSeason,
        heatingFuels: bd.winter.fuelType,
        heatingUnit: bd.winter.heatingUnit,
        internalTemp: bd.winter.internalTemp,
        project: await this.getProjectName()
      } : null,

      // ── API results ──
      summerResult: (this.selectedOption === 'summer' || this.selectedOption === 'both')
        ? (this.summerResults ? {
          diffCoolRequirements: this.summerResults.diffCoolRequirements,
          diffCoolCosts: this.summerResults.diffCoolCosts,
          diffCO2: this.summerResults.diffCO2
        } : null)
        : null,

      wiinterRsult: (this.selectedOption === 'winter' || this.selectedOption === 'both')
        ? (this.winterResults ? {
          diffCoolRequirements: this.winterResults.diffHeatRequirements,
          diffCoolCosts: this.winterResults.diffHeatCosts,
          diffCO2: this.winterResults.diffCO2
        } : null)
        : null,

      summerWinterResult: this.selectedOption === 'both' && this.bothResults ? {
        diffHeatCoolRequirements: this.bothResults.diffHeatCoolRequirements,
        diffCosts: this.bothResults.diffCosts,
        diffCO2: this.bothResults.diffCO2
      } : null
    };

    try {
      await this.storageService.updateProject({ energySavingsResult });
      console.log('Energy savings result saved:', energySavingsResult);
    } catch (error) {
      console.error('Error saving energy savings result:', error);
    }

    this.modalController.dismiss(null, 'confirm');
  }


  async incrementQty() {
    if (this.eQty < this.maxQty) {
      this.eQty = Math.min(this.maxQty, this.eQty + this.stepCount);
      // await this.updateTotalWindowArea();
      this.validateForm();
    }
  }
  async decrementQty() {
    if (this.eQty > this.minQty) {
      this.eQty = Math.max(this.minQty, this.eQty - this.stepCount);
      // await this.updateTotalWindowArea();
      this.validateForm();
    }
  }

  incrementYrs() {
    if (this.eYrs < this.maxYrs) {
      this.eYrs = Math.min(this.maxYrs, this.eYrs + this.stepCount);
      this.validateForm();
    }
  }
  decrementYrs() {
    if (this.eYrs > this.minYrs) {
      this.eYrs = Math.max(this.minYrs, this.eYrs - this.stepCount);
      this.validateForm();
    }
  }
  validateForm(): void {
    console.log('validateForm called');

    const haseQty = this.eQty !== null && this.eQty !== undefined;
    const haseYrs = this.eYrs !== null && this.eYrs !== undefined && this.eYrs > 0;
    const hasBasicData = this.basicData !== null;
    this.isFormValid = haseQty && haseYrs && hasBasicData;
    this.updateTotalWindowArea();
    console.log('Form valid:', this.isFormValid, {
      eQty: this.eQty,
      eYrs: this.eYrs,
      hasBasicData,
      basicData: this.basicData
    });

    // Only emit if values have changed
    if (
      this.previousValues.eQty !== this.eQty ||
      this.previousValues.eYrs !== this.eYrs ||
      this.previousValues.selectedOption !== this.selectedOption ||
      JSON.stringify(this.previousValues.basicData) !== JSON.stringify(this.basicData)
    ) {
      console.log('Values changed, triggering calculation');

      // Emit with current values
      this.calculationTrigger$.next({
        eQty: this.eQty,
        eYrs: this.eYrs,
        selectedOption: this.selectedOption,
        basicData: this.basicData
      });

      // Update previous values
      this.previousValues = {
        eQty: this.eQty,
        eYrs: this.eYrs,
        selectedOption: this.selectedOption,
        basicData: this.basicData ? JSON.parse(JSON.stringify(this.basicData)) : null
      };
    } else {
      console.log('No value change detected');
    }
  }

  close() {
    this.modalController.dismiss();
  }

  async openBasicdata() {
    const modal = await this.modalController.create({
      component: BasicDataEnergySavingsComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        selectedSeason: this.selectedOption
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      this.basicData = data;
      console.log('Received basic data:', this.basicData);
      this.validateForm();
    }
  }

  async clickSeeComparedConfig() {
    const modal = await this.modalController.create({
      component: ConfigurationPage,
      cssClass: 'configuration-modal',
      componentProps: {
        isComparisonMode: true
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      await this.performCalculations();
    }
  }

  async performCalculations() {
    if (!this.isFormValid) {
      return;
    }

    try {
      // Get window data
      const { window1, window2 } = await this.getWindowData();

      this.hasError = false;
      this.errorMessage = '';

      if (this.selectedOption === 'winter' && this.basicData?.winter) {
        const payload: WinterCalcRequest = {
          project: await this.getProjectName(),
          externalTemp: this.basicData.winter.externalTemp,
          internalTemp: this.basicData.winter.internalTemp,
          cost: this.basicData.winter.heatingCost,
          currency: this.basicData.winter.heatingCurrency,
          efficiency: this.basicData.winter.efficiency,
          costDelta: this.basicData.winter.costDelta,
          window1,
          window2,
          windowsCount: this.eQty,
          yearsCount: this.eYrs,
          heatSeason: this.basicData.winter.heatSeason,
          heatingFuels: this.basicData.winter.fuelType,
          heatingUnit: this.basicData.winter.heatingUnit,
          calorific: this.basicData.winter.calorificValue
        };

        this.winterResults = (await this.energySavingsApi.calculateWinter(payload).toPromise()) ?? null;

      } else if (this.selectedOption === 'summer' && this.basicData?.summer) {
        const payload: SummerCalcRequest = {
          project: await this.getProjectName(),
          externalTemp: this.basicData.summer.externalTemp,
          internalTemp: this.basicData.summer.internalTemp,
          cost: this.basicData.summer.electricalCost,
          currency: this.basicData.summer.electricalCurrency,
          efficiency: this.basicData.summer.efficiency,
          costDelta: this.basicData.summer.costDelta,
          window1,
          window2,
          windowsCount: this.eQty,
          yearsCount: this.eYrs,
          coolSeason: this.basicData.summer.coolSeason,
          power: this.basicData.summer.powerType
        };

        this.summerResults = (await this.energySavingsApi.calculateSummer(payload).toPromise()) ?? null;

      } else if (this.selectedOption === 'both' && this.basicData?.winter && this.basicData?.summer) {
        const winterData = {
          project: await this.getProjectName(),
          externalTemp: this.basicData.winter.externalTemp,
          internalTemp: this.basicData.winter.internalTemp,
          cost: this.basicData.winter.heatingCost,
          currency: this.basicData.winter.heatingCurrency,
          efficiency: this.basicData.winter.efficiency,
          costDelta: this.basicData.winter.costDelta,
          window1,
          window2,
          windowsCount: this.eQty,
          yearsCount: this.eYrs,
          heatSeason: this.basicData.winter.heatSeason,
          heatingFuels: this.basicData.winter.fuelType,
          heatingUnit: this.basicData.winter.heatingUnit,
          calorific: this.basicData.winter.calorificValue
        };

        const summerData = {
          project: await this.getProjectName(),
          externalTemp: this.basicData.summer.externalTemp,
          internalTemp: this.basicData.summer.internalTemp,
          cost: this.basicData.summer.electricalCost,
          currency: this.basicData.summer.electricalCurrency,
          efficiency: this.basicData.summer.efficiency,
          costDelta: this.basicData.summer.costDelta,
          window1,
          window2,
          windowsCount: this.eQty,
          yearsCount: this.eYrs,
          coolSeason: this.basicData.summer.coolSeason,
          power: this.basicData.summer.powerType
        };

        this.bothResults = (await this.energySavingsApi.calculateWinterSummer({
          winterData,
          summerData,
          window1,
          window2
        }).toPromise()) ?? null;
        if (this.bothResults) {
          this.summerResults = this.bothResults?.summer;
          this.winterResults = this.bothResults?.winter;
        }
      }

    } catch (error: any) {
      console.error('Energy savings calculation error:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Failed to calculate energy savings. Please try again.';
    } finally {
    }
  }

  private async getWindowData() {
    return await this.windowPayloadBuilder.buildWindowPayloads();
  }

  hasResults(): boolean {
    if (this.selectedOption === 'winter') {
      return this.winterResults !== null;
    } else if (this.selectedOption === 'summer') {
      return this.summerResults !== null;
    } else if (this.selectedOption === 'both') {
      return this.bothResults !== null;
    }
    return false;
  }

  getTotalEnergyCostSavings(): number {
    if (this.selectedOption === 'winter' && this.winterResults) {
      return this.winterResults.diffHeatCosts;
    } else if (this.selectedOption === 'summer' && this.summerResults) {
      return this.summerResults.diffCoolCosts;
    } else if (this.selectedOption === 'both' && this.bothResults) {
      return this.bothResults.diffCosts;
    }
    return 0;
  }

  getTotalCO2Savings(): number {
    if (this.selectedOption === 'winter' && this.winterResults) {
      return this.winterResults.diffCO2;
    } else if (this.selectedOption === 'summer' && this.summerResults) {
      return this.summerResults.diffCO2;
    } else if (this.selectedOption === 'both' && this.bothResults) {
      return this.bothResults.diffCO2;
    }
    return 0;
  }

  getCurrency(): string {
    if (this.selectedOption === 'winter' && this.basicData?.winter) {
      return this.basicData.winter.heatingCurrency;
    } else if (this.selectedOption === 'summer' && this.basicData?.summer) {
      return this.basicData.summer.electricalCurrency;
    } else if (this.selectedOption === 'both' && this.basicData?.winter) {
      return this.basicData.winter.heatingCurrency;
    }
    return '€';
  }

  private async getProjectName(): Promise<string> {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        return project.projectName || 'Default Project';
      }
    } catch (error) {
      console.error('Error getting project name:', error);
    }
    return 'Default Project';
  }

  private async setDefaultBasicData(): Promise<void> {
    this.basicData = {
      season: this.selectedOption,
      winter: {
        fuelType: DEFAULT_ENERGY_SAVINGS_DATA.winter.fuelType,
        heatingCost: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCost,
        heatingCurrency: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCurrency,
        calorificValue: DEFAULT_ENERGY_SAVINGS_DATA.winter.calorificValue,
        heatingUnit: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingUnit,
        heatSeason: DEFAULT_ENERGY_SAVINGS_DATA.winter.heatSeason,
        internalTemp: DEFAULT_ENERGY_SAVINGS_DATA.winter.internalTemp,
        externalTemp: DEFAULT_ENERGY_SAVINGS_DATA.winter.externalTemp,
        efficiency: DEFAULT_ENERGY_SAVINGS_DATA.winter.efficiency,
        costDelta: DEFAULT_ENERGY_SAVINGS_DATA.winter.costDelta
      }
    };

    // Save to storage
    try {
      await this.storageService.updateProject({
        energyBasicData: this.basicData
      });
      console.log('Default basicData saved to storage');
    } catch (error) {
      console.error('Error saving default basicData:', error);
    }
  }

  private async updateTotalWindowArea(projectData?: any) {
    if (!projectData) {
      const res = await this.storageService.getProject();
      projectData = res ? JSON.parse(res) : null;
    }

    if (!projectData) {
      this.totalWindowArea = '0.00';
      return;
    }

    const windowTypeId = projectData.windowtype?.id || 1;
    const dimA = projectData.dimensions.a || 1.23;
    const dimB = projectData.dimensions.b || 1.48;
    const dimC = projectData.dimensions.c || 0;

    let singleWindowArea = 0;

    // Calculate based on window type
    if ([1, 2, 3, 4, 9, 10, 13, 14].includes(windowTypeId)) {
      singleWindowArea = dimA * dimB;
    } else if ([5, 7, 8, 11, 12].includes(windowTypeId)) {
      singleWindowArea = dimA * (dimB + dimC);
    } else if (windowTypeId === 6) {
      singleWindowArea = (dimA + dimB) * dimC;
    } else {
      singleWindowArea = dimA * dimB;
    }

    // Total area = single window × quantity
    const totalArea = singleWindowArea * this.eQty;
    this.totalWindowArea = totalArea.toFixed(2);

    console.log('Window area calculated:', {
      singleWindowArea: singleWindowArea.toFixed(2),
      quantity: this.eQty,
      totalArea: this.totalWindowArea
    });
  }
  ngOnDestroy() {
    if (this.calculationSubscription) {
      this.calculationSubscription.unsubscribe();
    }
  }
}
